import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ShieldCheck, Zap, Download, Award, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [best, setBest] = useState([]);
  const [newArr, setNewArr] = useState([]);
  const [email, setEmail] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/products?best_seller=true").then((r) => setBest(r.data.slice(0, 4)));
    api.get("/products?new_arrival=true").then((r) => setNewArr(r.data.slice(0, 4)));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("Welcome to PLAYA"); setEmail(""); }
    catch { toast.error("Please enter a valid email"); }
  };

  const faqs = [
    { q: "How fast is delivery?", a: "All PLAYA products are digital and delivered instantly to your dashboard the moment payment is confirmed." },
    { q: "Are your products officially licensed?", a: "Yes. PLAYA operates as an authorized reseller and every listing respects trademark and IP rights." },
    { q: "Can I access downloads later?", a: "Absolutely. Every purchase lives forever in your customer dashboard, downloadable up to 10 times." },
    { q: "Do you offer refunds?", a: "Digital goods are non-refundable once downloaded. Exceptional cases are honored within 7 days." },
  ];

  const reviews = [
    { name: "Amelia R.", role: "Creative Director", text: "The Golden Ratio Design Pack changed our studio's workflow overnight. Immaculate curation." },
    { name: "Julien D.", role: "Founder, Maison Nord", text: "PLAYA feels like a private club. The Founder Toolkit is worth 10x the price." },
    { name: "Rhea S.", role: "Stylist", text: "The lookbooks are simply unmatched. Editorial-grade, delivered instantly." },
  ];

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="relative hero-grain overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.pexels.com/photos/31622917/pexels-photo-31622917.jpeg" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/70 via-[#0B0B0B]/50 to-[#0B0B0B]"></div>
        </div>
        <div className="container-luxe pt-32 pb-40">
          <div className="fade-up max-w-4xl">
            <div className="badge-gold inline-block mb-8">Licensed Digital Reseller</div>
            <h1 data-testid="hero-title" className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tighter text-center w-fit">
              #1 Marketplace for<br />
              <span className="gold-gradient-text">1:1 Vendors</span>
            </h1>
            <p data-testid="hero-subtitle" className="mt-8 text-lg text-gray-300 max-w-xl leading-relaxed">
              Instant delivery of premium digital products. Curated by connoisseurs, delivered in seconds, licensed to last.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="cta-shop-now" to="/shop" className="btn-gold">Shop Now <ArrowRight size={16} /></Link>
              <Link data-testid="cta-browse-categories" to="/categories" className="btn-ghost">Browse Categories</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <Section title="Featured Products" subtitle="Handpicked for the connoisseur">
        <ProductGrid products={featured} />
      </Section>

      {/* BEST SELLERS */}
      <Section title="Best Sellers" subtitle="Loved by the discerning">
        <ProductGrid products={best} />
      </Section>

      {/* NEW ARRIVALS */}
      <Section title="New Arrivals" subtitle="The latest to enter the vault">
        <ProductGrid products={newArr} />
      </Section>

      {/* WHY CHOOSE */}
      <section className="container-luxe py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Why PLAYA</div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">A boutique built on trust</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: Zap, t: "Instant Delivery", d: "Access your products the moment payment clears." },
            { icon: ShieldCheck, t: "Secure Checkout", d: "PCI-compliant Stripe processing, always encrypted." },
            { icon: Download, t: "Digital Product", d: "No shipping. No waiting. Just impeccable files." },
            { icon: Award, t: "Licensed Seller", d: "Authorized reseller with verified provenance." },
          ].map((f) => (
            <div key={f.t} data-testid={`feature-${f.t.toLowerCase().replace(/\s/g, "-")}`} className="glass rounded-2xl p-8">
              <f.icon className="text-[#D4AF37] mb-6" size={28} strokeWidth={1.5} />
              <div className="font-display text-lg font-medium">{f.t}</div>
              <p className="text-sm text-gray-400 mt-3 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="container-luxe py-24 border-t border-white/5">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#00A86B] mb-4">Testimonials</div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">In the words of our clients</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r) => (
            <div key={r.name} className="glass rounded-2xl p-8">
              <div className="flex gap-1 text-[#D4AF37] mb-4">{Array.from({ length: 5 }).map((_, i) => <span key={i}>★</span>)}</div>
              <p className="text-gray-300 leading-relaxed italic">"{r.text}"</p>
              <div className="mt-6 text-sm">
                <div className="text-white font-medium">{r.name}</div>
                <div className="text-gray-500 text-xs tracking-widest uppercase">{r.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container-luxe py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">FAQ</div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Questions, answered</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <button key={i} data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full text-left glass rounded-xl p-6 hover:border-[#D4AF37]/30 transition">
                <div className="flex justify-between items-center gap-4">
                  <span className="font-display text-lg">{f.q}</span>
                  <ChevronDown className={`transition ${openFaq === i ? "rotate-180 text-[#D4AF37]" : "text-gray-500"}`} size={18} />
                </div>
                {openFaq === i && <p className="mt-4 text-gray-400 leading-relaxed">{f.a}</p>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="container-luxe py-24 border-t border-white/5">
        <div className="glass rounded-3xl p-12 md:p-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Join the atelier</h2>
          <p className="text-gray-400 mt-4 max-w-xl mx-auto">Get early access to new arrivals and members-only drops.</p>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input data-testid="newsletter-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@atelier.com" />
            <button data-testid="newsletter-submit" className="btn-gold whitespace-nowrap">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="container-luxe py-24">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">{subtitle}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">{title}</h2>
        </div>
        <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37]">View all <ArrowRight size={14} /></Link>
      </div>
      {children}
    </section>
  );
}

function ProductGrid({ products }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}
